import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store/store";
import { total, discount, tax, netTotal } from "../../store/pos/posSelector";
import { getSocket } from "../../utils/socket/socketClient";
import { getDecryptedToken } from "../../utils/crypto/cryptoUtils";
import { getCurrencySymbol } from "../../utils/currency/currencyUtils";
import "./CustomerView.scss";
import Promotions from "./Promotions";
import { getMediaUrl } from "../../utils/mediaUrl";

function CustomerView() {
  const dispatch = useDispatch();
  const { addedItemArray } = useSelector(
    (state: RootState) => state.pos.current,
  );
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const previousItemCountRef = React.useRef(0);

  // read shop details from token
  const shopName =
    getDecryptedToken("martName") || getDecryptedToken("name") || "Shop";
  const address =
    getDecryptedToken("address") || getDecryptedToken("martAddress") || "";
  const phoneNumber =
    getDecryptedToken("phoneNumber") ||
    getDecryptedToken("martPhone") ||
    getDecryptedToken("phone") ||
    "";
  const ownerName = getDecryptedToken("name") || "";
  const email = getDecryptedToken("email") || "";
  const currency = getDecryptedToken("currency") || "";
  const currencySymbol = getCurrencySymbol();

  const totalAmount = useSelector(total);
  const discountAmount = useSelector(discount);
  const taxAmount = useSelector(tax);
  const netTotalAmount = useSelector(netTotal);

  const [now, setNow] = React.useState<Date>(new Date());
  const [animateItems, setAnimateItems] = React.useState(false);

  React.useEffect(() => {
    const socket = getSocket();
    const handlePosUpdate = (payload: any) => {
      try {
        const myMartId =
          getDecryptedToken("selectedMartId") ||
          getDecryptedToken("selectedMartId") ||
          "";
        const myCompanyId =
          getDecryptedToken("companyId") ||
          getDecryptedToken("companyID") ||
          "";

        if (!payload) return;

        if (Array.isArray(payload)) {
          return;
        }

        const incomingMartId = String(payload.martId || "");
        const incomingCompanyId = String(payload.companyId || "");
        if (
          incomingMartId === String(myMartId) &&
          incomingCompanyId === String(myCompanyId)
        ) {
          const items = payload.items || [];
          dispatch({
            type: "pos/updateAddedItemArrayFromSocket",
            payload: items,
          });
          setAnimateItems(true);
          setTimeout(() => setAnimateItems(false), 500);
        }
      } catch (err) {
        console.error("pos_update handler error:", err);
      }
    };
    socket.on("pos_update", handlePosUpdate);

    const timer = setInterval(() => setNow(new Date()), 1000);

    return () => {
      socket.off("pos_update", handlePosUpdate);
      clearInterval(timer);
    };
  }, [dispatch]);

  React.useEffect(() => {
    const currentCount = addedItemArray?.length || 0;
    const previousCount = previousItemCountRef.current;

    if (currentCount > previousCount && tableContainerRef.current) {
      tableContainerRef.current.scrollTo({
        top: tableContainerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }

    previousItemCountRef.current = currentCount;
  }, [addedItemArray]);

  const hasItems = addedItemArray && addedItemArray.length > 0;

  if (!hasItems) {
    return (
      // <div className="customer-view-container">
      <Promotions />
      // </div>
    );
  }

  const itemCount = addedItemArray?.reduce(
    (sum: number, item: any) => sum + (item.quantity || 0),
    0,
  );

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString([], {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="customer-view-container">
      <div className="split-grid">
        <div className="left-column left-scroll">
          <div className="order-header">
            <h1 className="customer-view-title">Your Order</h1>
            <div className="item-count-badge">
              {itemCount} {itemCount === 1 ? "Item" : "Items"}
            </div>
          </div>

          <div
            className={`customer-table-wrapper ${animateItems ? "pulse" : ""}`}
            ref={tableContainerRef}
          >
            <table className="customer-table">
              <thead>
                <tr>
                  <th className="col-no">#</th>
                  <th className="col-name">Item</th>
                  <th className="col-qty">Qty</th>
                  <th className="col-price">Price</th>
                  <th className="col-discount">Disc</th>
                  <th className="col-total">Total</th>
                </tr>
              </thead>
              <tbody>
                {addedItemArray?.map((item: any, i: number) => {
                  const itemTotal =
                    item.price * item.quantity -
                    (item.discount * item.quantity || 0);
                  return (
                    <tr key={i} className="item-row">
                      <td className="col-no">{i + 1}</td>
                      <td className="col-name">
                        <div className="item-name-wrapper">
                          {item.image && (
                            <img
                              src={getMediaUrl(item.image)}
                              alt={item.name}
                              className="item-thumbnail"
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display =
                                  "none";
                              }}
                            />
                          )}
                          <div className="item-text">
                            <span className="item-name">{item.name}</span>
                            {item.notes && (
                              <span className="item-notes">{item.notes}</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="col-qty">
                        <span className="qty-badge">x{item.quantity}</span>
                      </td>
                      <td className="col-price">
                        {currency} {Number(item.price).toFixed(2)}
                      </td>
                      <td className="col-discount">
                        {item.discount > 0 ? (
                          <span className="discount-badge">
                            -{currency}{" "}
                            {(item.discount * item.quantity).toFixed(2)}
                          </span>
                        ) : (
                          <span className="no-discount">-</span>
                        )}
                      </td>
                      <td className="col-total">
                        <strong>
                          {currency} {itemTotal.toFixed(2)}
                        </strong>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="customer-summary">
            <div className="summary-row subtotal">
              <span>Subtotal:</span>
              <span>
                {currency} {totalAmount.toFixed(2)}
              </span>
            </div>
            {discountAmount > 0 && (
              <div className="summary-row discount">
                <span>Discount:</span>
                <span className="discount-amount">
                  - {currency} {discountAmount.toFixed(2)}
                </span>
              </div>
            )}
            <div className="summary-row tax">
              <span>Tax:</span>
              <span>
                {currency} {taxAmount.toFixed(2)}
              </span>
            </div>
            <div className="summary-divider"></div>
            <div className="summary-row total">
              <span>Total Amount:</span>
              <span className="total-amount">
                {currency} {netTotalAmount.toFixed(2)}
              </span>
            </div>
          </div>

          <div className="thank-you-message">Thank you for your purchase!</div>
        </div>

        <aside className="right-panel">
          <div className="shop-card">
            <div className="shop-header">
              <div className="shop-logo">
                {shopName?.slice(0, 2).toUpperCase()}
              </div>
              <div className="shop-info">
                <h2 className="shop-name">{shopName}</h2>
                {ownerName && (
                  <div className="shop-meta">Served by: {ownerName}</div>
                )}
              </div>
            </div>

            <div className="time-display">
              <div className="time">{formatTime(now)}</div>
              <div className="date">{formatDate(now)}</div>
            </div>

            <div className="shop-details">
              {address && (
                <div className="detail-row">
                  <svg
                    className="detail-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span>{address}</span>
                </div>
              )}
              {phoneNumber && (
                <div className="detail-row">
                  <svg
                    className="detail-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span>{phoneNumber}</span>
                </div>
              )}
              {email && (
                <div className="detail-row">
                  <svg
                    className="detail-icon"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <span>{email}</span>
                </div>
              )}
            </div>
          </div>

          <Promotions isInline={true} />

          <div className="powered-by">
            Powered by <strong>VisoLabs</strong>
            <br />
            <a
              href="https://visolabs.io"
              target="_blank"
              rel="noopener noreferrer"
            >
              visolabs.io
            </a>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default CustomerView;
